import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CreateProfileLinkRequest, ProfileLinkPublic } from "@/types";

export function useProfileLinks() {
  return useQuery({
    queryKey: ["profile-links"],
    queryFn: api.profileLinks.list,
  });
}

export function useCreateProfileLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProfileLinkRequest) => api.profileLinks.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["profile-links"] }),
  });
}

export function useRevokeProfileLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.profileLinks.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["profile-links"] }),
  });
}

// Public metadata for the /p/:token landing page (no auth, no redirect on 401).
export function usePublicProfileLink(token?: string) {
  return useQuery({
    queryKey: ["profile-link-public", token],
    enabled: !!token,
    retry: false,
    queryFn: async () => {
      const res = await fetch(`/api/profile-links/${encodeURIComponent(token!)}`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Link is invalid or has expired.");
      return (await res.json()) as ProfileLinkPublic;
    },
  });
}
